import React from 'react';
import { ArrowUpRight, HandHeart, Link2, Shield } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  exchangeGoogleOAuthCode,
  getCurrentUser,
  isAuthenticated,
} from '@/lib/api/auth';
import { getDefaultAuthenticatedRoute } from '@/lib/accessControl';
import { consumeGoogleOAuthIntent, getGoogleFrontendRedirectUri } from '@/lib/oauth/google';
import {
  buildSignupConsentSnapshot,
  clearStoredSignupConsent,
  getSignupConsentUpdatePayload,
  readStoredSignupConsent,
} from '@/lib/signupConsent';
import { getIanaTimeZone } from '@/utils/appointments/appointment-timezone';
import {
  REGISTRATION_BLOCKED_MESSAGE,
  REGISTRATION_NOT_ALLOWED_CODE,
  isRegistrationNotAllowedError,
} from '@/lib/auth/registrationGate';
import { showErrorToast, showWarningToast } from '@/utils/toast-utils';
import headlinesLogo from '@/assets/images/headlines-logo.svg';

const authBackground = '/images/auth/background.svg';
const GOOGLE_SIGNUP_ONBOARDING_ROUTE = '/onboarding/practice-type/institution';

const onboardingSteps = [
  {
    title: 'Step 1',
    heading: 'Tell us about your practice',
    description: 'Share quick details so we can tailor the platform to your workflow.',
    icon: Link2,
  },
  {
    title: 'Step 2',
    heading: 'See your personalised dashboard',
    description: 'Your workspace is ready with structured insights and tailored workflows.',
    icon: HandHeart,
  },
  {
    title: 'Step 3',
    heading: 'Add your first client',
    description: 'Set up their profile to initiate planning, monitoring, and compliance tracking.',
    icon: Shield,
  },
] as const;

const getMergedParams = (searchParams: URLSearchParams): URLSearchParams => {
  const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  const merged = new URLSearchParams(searchParams.toString());

  hashParams.forEach((value, key) => {
    if (!merged.has(key)) {
      merged.set(key, value);
    }
  });

  return merged;
};

const isSignupOAuthState = (params: URLSearchParams): boolean => {
  if (params.get('signupConsent') === 'accepted') {
    return true;
  }

  const state = params.get('state')?.trim();
  if (!state) {
    return false;
  }

  return new URLSearchParams(state).get('signupConsent') === 'accepted';
};

const OnboardingWelcomePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isProcessing, setIsProcessing] = React.useState(true);
  const [hasValidSession, setHasValidSession] = React.useState(false);

  React.useEffect(() => {
    let isCancelled = false;

    const processOAuthPayload = async () => {
      const params = getMergedParams(searchParams);
      const token = params.get('token')?.trim();
      const code = params.get('code')?.trim() ?? '';
      const state = params.get('state')?.trim() ?? '';
      const oauthIntent = consumeGoogleOAuthIntent();
      const isSignupOAuth = oauthIntent === 'signup' || isSignupOAuthState(params);
      const errorMessage = params.get('errorMessage');
      if (errorMessage || params.has('error')) {
        window.history.replaceState({}, document.title, window.location.pathname);
        showErrorToast(
          errorMessage || 'Google sign-in was not completed. Please try Google Sign-In again.',
          'Google sign-in failed',
          isSignupOAuth ? undefined : { duration: 5000 },
        );
        navigate(isSignupOAuth ? '/signup' : '/login', { replace: true });
        return;
      }

      // OAuthHashSessionBridge owns token callbacks, including provider persistence.
      if (token) return;

      if (code === REGISTRATION_NOT_ALLOWED_CODE) {
        showWarningToast('Sign up unavailable', REGISTRATION_BLOCKED_MESSAGE);
        navigate('/signup', { replace: true, state: { registrationBlocked: true } });
        return;
      }

      if (code) {
        try {
          const signupConsent = isSignupOAuth
            ? readStoredSignupConsent() ?? buildSignupConsentSnapshot()
            : null;
          const response = await exchangeGoogleOAuthCode({
            code,
            redirectUri: getGoogleFrontendRedirectUri(),
            state,
            consent: signupConsent ? getSignupConsentUpdatePayload(signupConsent) : null,
            timezone: getIanaTimeZone(),
          });

          if (isCancelled) {
            return;
          }

          window.history.replaceState({}, document.title, window.location.pathname);
          if (signupConsent) {
            clearStoredSignupConsent();
          }

          if (response.memberStatus !== 'new') {
            navigate(getDefaultAuthenticatedRoute(getCurrentUser()), { replace: true });
            return;
          }

          navigate(GOOGLE_SIGNUP_ONBOARDING_ROUTE, { replace: true });
          return;
        } catch (error) {
          if (isCancelled) {
            return;
          }

          if (isRegistrationNotAllowedError(error)) {
            showWarningToast('Sign up unavailable', REGISTRATION_BLOCKED_MESSAGE);
            navigate('/signup', { replace: true, state: { registrationBlocked: true } });
            return;
          }

          showErrorToast(error, 'Google sign-in failed', isSignupOAuth ? undefined : { duration: 5000 });
          navigate(isSignupOAuth ? '/signup' : '/login', { replace: true });
          return;
        }
      }

      if (isAuthenticated() && getCurrentUser()) {
        setHasValidSession(true);
        setIsProcessing(false);
        return;
      }

      navigate(isSignupOAuth ? '/signup' : '/login', { replace: true });
    };

    void processOAuthPayload();

    return () => {
      isCancelled = true;
    };
  }, [navigate, searchParams]);

  if (isProcessing) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#e9edf1]">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 animate-spin rounded-full border-b-2 border-primary" />
          <p className="mt-4 text-gray-600">Setting up your account...</p>
        </div>
      </div>
    );
  }

  if (!hasValidSession) {
    return null;
  }

  return (
    <div className="min-h-dvh overflow-x-hidden bg-[#e9edf1] p-3 sm:p-5 2xl:p-8">
      <section
        className="relative mx-auto flex min-h-[calc(100dvh-1.5rem)] w-full overflow-hidden rounded-2xl border border-slate-200 px-4 py-5 shadow-[0_24px_80px_rgba(2,35,48,0.14)] sm:min-h-[calc(100dvh-2.5rem)] sm:px-6 sm:py-6 lg:px-8 lg:py-6 2xl:min-h-[calc(100dvh-4rem)] 2xl:px-14 2xl:py-12"
        style={{
          backgroundImage: `url(${authBackground})`,
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
        }}
      >
        <div className="relative z-10 flex w-full flex-col">
          <div className="mx-auto text-center text-white">
            <div className="mb-4 flex items-center justify-center gap-3 2xl:mb-6">
              <img src={headlinesLogo} alt="" aria-hidden="true" className="h-9 w-8 sm:h-10 sm:w-9 2xl:h-12 2xl:w-10" />
              <div className="text-left">
                <p className="text-3xl font-semibold leading-none tracking-tight sm:text-4xl 2xl:text-5xl">CalorieScience</p>
                <p className="mt-1 text-[11px] font-medium uppercase tracking-[0.22em] text-white/80 sm:text-xs">
                  Personalized. Intelligent. Scalable.
                </p>
              </div>
            </div>
            <h1 className="text-2xl font-semibold leading-tight sm:text-3xl 2xl:text-4xl">Welcome to CalorieScience</h1>
            <p className="mt-3 text-xs font-medium uppercase tracking-[0.2em] text-white/85 2xl:mt-5 sm:text-sm">
              You are all set. What&apos;s next?
            </p>
          </div>

          <div className="mt-5 grid gap-4 md:grid-cols-3 2xl:mt-10 2xl:gap-6">
            {onboardingSteps.map((item) => {
              const Icon = item.icon;
              return (
                <article key={item.title} className="flex h-full flex-col overflow-hidden rounded-xl bg-white shadow-[0_12px_36px_rgba(2,60,89,0.18)]">
                  <div className="px-5 pb-5 pt-5 sm:px-6 md:min-h-[220px] 2xl:min-h-[300px]">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#56BFE2]">
                      <Icon className="h-6 w-6 text-white" />
                    </div>
                    <h2 className="mt-4 text-[29px] font-semibold leading-[1.1] text-slate-900 sm:text-[34px] 2xl:text-[38px]">{item.title}</h2>
                    <p className="mt-3 text-base leading-7 text-slate-700 lg:text-[17px] 2xl:text-[19px] 2xl:leading-8">{item.heading}</p>
                  </div>
                  <div className="flex flex-1 items-start border-t border-[#E6F0F5] bg-[#E4F9FF] px-5 py-5 sm:px-6 2xl:py-7">
                    <p className="text-[14px] leading-6 text-slate-700 sm:text-[15px] sm:leading-7 2xl:text-base 2xl:leading-8">{item.description}</p>
                  </div>
                </article>
              );
            })}
          </div>

          <div className="mt-5 flex justify-center 2xl:mt-10">
            <Button
              type="button"
              onClick={() => navigate('/onboarding/practice-type/institution')}
              className="!h-[56px] !w-[266px] !rounded-[8px] !bg-white !px-[80px] !py-[16px] !text-xl !font-semibold !text-[#159FCB] hover:!bg-[#f3fbff]"
            >
              Continue
              <ArrowUpRight className="ml-2.5 h-6 w-6" />
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default OnboardingWelcomePage;
